import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { HttpService } from '../../../core/services/http.service';
import { ApiResponse } from '../../../core/models/api-response.model';
import {
  ClienteCoincidente, ConversionSolicitudWeb, ConvertirSolicitudWeb,
  EstadoSolicitudWeb, FiltrosSolicitudWeb, SolicitudWeb,
} from '../models/solicitud-web.model';

/**
 * Los contadores de la bandeja. Cada uno es el total que devuelve la lista al filtrar por esa
 * pastilla con el mismo texto de búsqueda; `habilitado` dice si el tenant tiene el canal.
 */
export interface StatsSolicitudesWeb {
  habilitado: boolean;
  total: number;
  nuevas: number;
  enRevision: number;
  cotizadas: number;
  agendadas: number;
  cerradas: number;
  descartadas: number;
  convertidas: number;
}

export interface PaginaSolicitudesWeb {
  content: SolicitudWeb[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/**
 * Las solicitudes que llegan desde la landing, vistas por administración.
 *
 * <p>Es un servicio aparte de `SolicitudesServicioService` a propósito: son otra API, otra tabla
 * y otro permiso (solo GESTION, porque hay datos personales). Mezclarlos escondería esa diferencia.
 */
@Injectable({ providedIn: 'root' })
export class SolicitudesWebService {
  private http = inject(HttpService);

  private readonly base = '/solicitudes-publicas';

  /** Estado y texto viajan al servidor: la lista y los contadores hablan de lo mismo. */
  listar(pagina: number, tamano: number, filtros: FiltrosSolicitudWeb = {},
         opciones?: { silencioso?: boolean }): Observable<ApiResponse<PaginaSolicitudesWeb>> {
    let params = new HttpParams().set('page', String(pagina)).set('size', String(tamano));
    if (filtros.estado) params = params.set('estado', filtros.estado);
    const texto = filtros.texto?.trim();
    if (texto) params = params.set('q', texto);
    // Silencioso: sin toast de error y sin los reintentos del arranque en frío. Para la navegación entre registros, que
    // pide la página siguiente de la bandeja y ya muestra su propia carga y su «Reintentar».
    return opciones?.silencioso
      ? this.http.getSilent<PaginaSolicitudesWeb>(this.base, params, { sinReintentos: true })
      : this.http.get<PaginaSolicitudesWeb>(this.base, params);
  }

  /** Por `getSilent`: son números de apoyo, y si fallan la pantalla se dibuja igual. */
  stats(texto = ''): Observable<ApiResponse<StatsSolicitudesWeb>> {
    let params = new HttpParams();
    if (texto.trim()) params = params.set('q', texto.trim());
    return this.http.getSilent<StatsSolicitudesWeb>(`${this.base}/stats`, params);
  }

  porId(id: number): Observable<ApiResponse<SolicitudWeb>> {
    return this.http.get<SolicitudWeb>(`${this.base}/${id}`);
  }

  cambiarEstado(id: number, estado: EstadoSolicitudWeb): Observable<ApiResponse<SolicitudWeb>> {
    return this.http.patch<SolicitudWeb>(`${this.base}/${id}/estado`, { estado });
  }

  /**
   * Clientes que podrían ser el mismo que el del lead (por el RUT escrito, el correo o el
   * teléfono). Es una ayuda para no duplicar: no vincula nada por sí sola. Por `getSilent`: si
   * falla, se puede convertir igual creando un cliente nuevo.
   */
  clientesCoincidentes(id: number, rut = ''): Observable<ApiResponse<ClienteCoincidente[]>> {
    let params = new HttpParams();
    if (rut.trim()) params = params.set('rut', rut.trim());
    return this.http.getSilent<ClienteCoincidente[]>(`${this.base}/${id}/clientes-coincidentes`, params);
  }

  /** Abre la solicitud de servicio y la liga a un cliente (existente o nuevo). */
  convertir(id: number, cuerpo: ConvertirSolicitudWeb): Observable<ApiResponse<ConversionSolicitudWeb>> {
    return this.http.post<ConversionSolicitudWeb>(`${this.base}/${id}/convertir`, cuerpo);
  }

  /**
   * La foto como archivo. Va por el cliente HTTP y no por un `<img src>`: la ruta exige el token
   * y el dominio del tenant, y un `<img>` los manda sin ninguno de los dos.
   */
  foto(id: number, archivoId: string): Observable<Blob> {
    return this.http.getBlob(`${this.base}/${id}/archivos/${encodeURIComponent(archivoId)}`);
  }
}
